import { defineStore } from 'pinia';
import { ref } from 'vue';
import type { Item } from '@gigdriver/domain';
import {
  getOrgItems,
  createItem as createItemApi,
  updateItem as updateItemApi,
  deleteItem as deleteItemApi,
  importInvoiceNinjaItems,
} from '@gigdriver/client';

export const useItemsStore = defineStore('items', () => {
  const items = ref<Item[]>([]);
  const isLoading = ref(false);
  const isImporting = ref(false);
  const error = ref<string | null>(null);

  async function loadOrgItems(orgId: string) {
    try {
      isLoading.value = true;
      error.value = null;
      items.value = await getOrgItems(orgId);
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to load items';
    } finally {
      isLoading.value = false;
    }
  }

  async function createItem(orgId: string, data: Omit<Item, 'id' | 'orgId' | 'createdAt' | 'updatedAt'>) {
    try {
      error.value = null;
      await createItemApi(orgId, data);
      await loadOrgItems(orgId);
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to add item';
      throw err;
    }
  }

  async function updateItem(orgId: string, itemId: string, updates: Partial<Item>) {
    try {
      error.value = null;
      await updateItemApi(orgId, itemId, updates);
      await loadOrgItems(orgId);
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to update item';
      throw err;
    }
  }

  async function deleteItem(orgId: string, itemId: string) {
    try {
      error.value = null;
      await deleteItemApi(orgId, itemId);
      items.value = items.value.filter(i => i.id !== itemId);
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to delete item';
      throw err;
    }
  }

  async function importFromInvoiceNinja(orgId: string) {
    try {
      isImporting.value = true;
      error.value = null;
      const result = await importInvoiceNinjaItems({ orgId });
      await loadOrgItems(orgId);
      return result;
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to import from Invoice Ninja';
      throw err;
    } finally {
      isImporting.value = false;
    }
  }

  return {
    items,
    isLoading,
    isImporting,
    error,
    loadOrgItems,
    createItem,
    updateItem,
    deleteItem,
    importFromInvoiceNinja,
  };
});
